class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.activeFetch = null;
    this._onCartChangeListener = this.handleCartChange.bind(this);
    this.init();
  }

  init() {
    this.bindEvents();
    // Hide any existing errors on initialization
    this.hideErrors();
    // Initialize with current cart data after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initializeWithCartData();
    }, 100);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this._onCartChangeListener) {
      document.removeEventListener('cart:change', this._onCartChangeListener);
    }
  }

  bindEvents() {
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.applyDiscount.bind(this));
    } else {
      console.log('No form found in cart-discount component');
    }

    // Bind input event to clear errors when user starts typing
    const discountInput = this.querySelector('input[name="discount"]');
    if (discountInput) {
      discountInput.addEventListener('input', this.hideErrors.bind(this));
    }

    // Bind remove buttons for existing discount pills
    this.bindRemoveButtons();
    
    // Listen for cart changes to update discount amounts
    document.addEventListener('cart:change', this._onCartChangeListener);
  }

  bindRemoveButtons() {
    const removeButtons = this.querySelectorAll('.cart-discount__pill-remove');
    removeButtons.forEach(button => {
      button.addEventListener('click', this.removeDiscount.bind(this));
    });
  }

  async initializeWithCartData() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        await this.updateComponentWithCartData(cartData);
        
        // Initialize discount amounts for existing elements
        if (cartData.cart_level_discount_applications) {
          this.updateDiscountApplications(cartData.cart_level_discount_applications);
        }
      } else {
        console.error('Failed to fetch cart data for initialization');
      }
    } catch (error) {
      console.error('Error initializing with cart data:', error);
    }
  }

  createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }

    const abortController = new AbortController();
    this.activeFetch = abortController;
    return abortController;
  }

  async applyDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;


    const abortController = this.createAbortController();

    try {
      const existingDiscounts = this.getExistingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) {
        // Code already exists, show a message or just return
        console.log('Discount code already applied');
        discountCode.value = '';
        return;
      }

      this.hideErrors();

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 422) {
          // Invalid discount code
          this.handleDiscountError('discount_code');
          discountCode.value = '';
          return;
        }
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      
      console.log('Cart update response:', data);

      // Check if discount was applied successfully
      let discountApplied = false;
      
      // Check discount_codes array
      if (data.discount_codes && data.discount_codes.length > 0) {
        console.log('Checking discount_codes:', data.discount_codes);
        const appliedDiscount = data.discount_codes.find(discount => 
          discount.code === discountCodeValue && discount.applicable === true
        );
        if (appliedDiscount) {
          console.log('Found in discount_codes:', appliedDiscount);
          discountApplied = true;
        }
      }
      
      // Check discount_applications array
      if (data.discount_applications && data.discount_applications.length > 0) {
        console.log('Checking discount_applications:', data.discount_applications);
        const appliedDiscount = data.discount_applications.find(discount => 
          discount.title === discountCodeValue
        );
        if (appliedDiscount) {
          console.log('Found in discount_applications:', appliedDiscount);
          discountApplied = true;
        }
      }
      
      // Check cart_level_discount_applications array
      if (data.cart_level_discount_applications && data.cart_level_discount_applications.length > 0) {
        console.log('Checking cart_level_discount_applications:', data.cart_level_discount_applications);
        const appliedDiscount = data.cart_level_discount_applications.find(discount => 
          discount.title === discountCodeValue
        );
        if (appliedDiscount) {
          console.log('Found in cart_level_discount_applications:', appliedDiscount);
          discountApplied = true;
        }
      }

      console.log('Final discount applied check:', discountApplied);
      
      if (!discountApplied) {
        console.log('Discount not applied, showing error');
        this.handleDiscountError('discount_code');
        discountCode.value = '';
        return;
      }
      
      console.log('Discount applied successfully');
      
      // Clear the input
      discountCode.value = '';
      
      // Get updated cart data and update component
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        await this.updateComponentWithCartData(cartData);
        
        // Update discount amounts immediately after successful application
        setTimeout(() => {
          this.updateDiscountAmounts();
        }, 100);
        
        // Trigger cart change event to update other components
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'discount:applied',
            cart: cartData
          }
        }));
        
        // Trigger cart updated event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        
        // Safe refresh after discount application
        setTimeout(() => {
          if (typeof safeInitQuantityControls === 'function') {
            safeInitQuantityControls();
          } else if (typeof initQuantityControls === 'function') {
            initQuantityControls();
          }
        }, 500);

      } else {
        console.error('Failed to fetch updated cart data');
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error applying discount:', error);
        // Show error for network errors or invalid discount codes
        this.handleDiscountError('discount_code');
        discountCode.value = '';
      }
    } finally {
      this.activeFetch = null;
    }
  }

  async removeDiscount(event) {
    event.preventDefault();
    event.stopPropagation();

    const pill = event.target.closest('.cart-discount__pill');
    if (!pill) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;


    const existingDiscounts = this.getExistingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.createAbortController();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      // Get updated cart data to refresh the component
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        await this.updateComponentWithCartData(cartData);
        
        // Trigger cart change event to update other components
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'discount:removed',
            cart: cartData
          }
        }));
        
        // Trigger cart updated event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        
        // Safe refresh after discount removal
        setTimeout(() => {
          if (typeof safeInitQuantityControls === 'function') {
            safeInitQuantityControls();
          } else if (typeof initQuantityControls === 'function') {
            initQuantityControls();
          }
        }, 500);
      } else {
        console.error('Failed to fetch updated cart data after removal');
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error removing discount:', error);
      }
    } finally {
      this.activeFetch = null;
    }
  }

  async updateComponentWithCartData(cartData) {
    try {
      
      // Save cart data globally for access in other methods
      window.cartData = cartData;
      
      // Try different ways to get discount codes
      let discountCodes = [];
      
      // Method 1: Try discount_applications (only applicable ones)
      if (cartData.discount_applications && Array.isArray(cartData.discount_applications)) {
        discountCodes = cartData.discount_applications
          .filter(app => app.applicable !== false) // Only include applicable discounts
          .map(app => app.title);
      }
      
      // Method 2: Try discount_codes (only if no codes found yet and only applicable ones)
      if (discountCodes.length === 0 && cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
        discountCodes = cartData.discount_codes
          .filter(code => {
            if (typeof code === 'object') {
              return code.applicable !== false; // Only include applicable discounts
            }
            return true; // If it's a string, assume it's applicable
          })
          .map(code => {
            if (typeof code === 'object' && code.code) {
              return code.code;
            }
            return code;
          });
      }
      
      // Method 3: Try cart_level_discount_applications (only if no codes found yet)
      if (discountCodes.length === 0 && cartData.cart_level_discount_applications && Array.isArray(cartData.cart_level_discount_applications)) {
        discountCodes = cartData.cart_level_discount_applications
          .filter(app => app.applicable !== false) // Only include applicable discounts
          .map(app => app.title);
      }
      
      // Method 4: Check if there are any discount-related properties
      const discountKeys = Object.keys(cartData).filter(key => key.includes('discount'));
      
      console.log('Found discount codes:', discountCodes);
      console.log('Cart data discount keys:', discountKeys);
      
      // Validate and filter out any invalid codes
      const validDiscountCodes = await this.validateDiscountCodes(discountCodes, cartData);
      console.log('Valid discount codes:', validDiscountCodes);
      
      // If we have invalid codes, remove them from the cart
      if (validDiscountCodes.length < discountCodes.length) {
        console.log('Removing invalid discount codes from cart');
        await this.removeInvalidDiscounts(discountCodes, validDiscountCodes);
      }
      
      this.updateDiscountPills(validDiscountCodes);

      // Update cart totals in cart drawer
      this.updateCartTotals(cartData);

      // Update cart count
      this.updateCartCount(cartData.item_count);
      
      // Update discount amounts with fresh cart data
      setTimeout(() => {
        this.updateDiscountAmounts();
      }, 50);
      


    } catch (error) {
      console.error('Error updating component:', error);
    }
  }

  updateDiscountPills(discountCodes) {
    const codesList = this.querySelector('.cart-discount__codes');
    if (!codesList) {
      console.log('No cart-discount__codes list found');
      return;
    }

    // Clear existing pills
    codesList.innerHTML = '';

    // Add new pills
    discountCodes.forEach(code => {
      const pill = document.createElement('li');
      pill.className = 'cart-discount__pill';
      pill.dataset.discountCode = code;
      pill.setAttribute('aria-label', `Discount ${code} applied`);

      // Find discount amount for this code
      let discountAmount = '';
      if (window.cartData && window.cartData.cart_level_discount_applications) {
        const discountApp = window.cartData.cart_level_discount_applications.find(app => app.title === code);
        if (discountApp) {
          discountAmount = ` -${this.formatMoney(discountApp.total_allocated_amount)}`;
        }
      }

      pill.innerHTML = `
        
        <p class="cart-discount__pill-code">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M3.75542 0.000609069C4.03906 -0.00917218 4.31403 0.0992153 4.51472 0.2999L9.07509 4.86028C9.47497 5.26014 9.47497 5.90845 9.07509 6.30833L6.30833 9.07511C5.90845 9.47497 5.26014 9.47497 4.86028 9.07509L0.2999 4.51472C0.0992153 4.31403 -0.00917218 4.03906 0.000609069 3.75541L0.0928356 1.08086C0.111379 0.543056 0.543059 0.111379 1.08086 0.0928341L3.75542 0.000609069ZM4.07278 0.741842C3.99459 0.663654 3.88747 0.621426 3.77695 0.625237L1.1024 0.717464C0.892872 0.724689 0.724689 0.89287 0.717464 1.1024L0.625237 3.77695C0.621426 3.88745 0.663654 3.99459 0.741842 4.07278L5.30222 8.63316C5.45801 8.78895 5.71059 8.78895 5.86637 8.63316L8.63316 5.86637C8.78895 5.71059 8.78895 5.45801 8.63316 5.30222L4.07278 0.741842ZM1.96773 1.96773C2.29814 1.63733 2.83384 1.63733 3.16425 1.96773C3.49467 2.29814 3.49467 2.83384 3.16425 3.16425C2.83384 3.49467 2.29814 3.49467 1.96773 3.16425C1.63733 2.83384 1.63733 2.29814 1.96773 1.96773ZM2.72231 2.40969C2.63598 2.32334 2.49602 2.32334 2.40969 2.40969C2.32334 2.49602 2.32334 2.63598 2.40969 2.72231C2.49602 2.80864 2.63598 2.80864 2.72231 2.72231C2.80864 2.63598 2.80864 2.49602 2.72231 2.40969Z" fill="#505050"/>
            </svg>
            ${code}
            <button
            type="button"
            class="cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled"
            aria-label="Remove discount ${code}"
            >
            ✕
            </button>
        </p>
        
        
        <span class="text-subdued discount-amount">${discountAmount}</span>
      `;

      codesList.appendChild(pill);
    });

    // Re-bind remove buttons
    this.bindRemoveButtons();
    
    // Update discount amounts after a short delay to ensure cart data is fully updated
    setTimeout(() => {
      this.updateDiscountAmounts();
    }, 100);
  }

  updateDiscountAmounts() {
    if (!window.cartData || !window.cartData.cart_level_discount_applications) {
      return;
    }

    const discountPills = this.querySelectorAll('.cart-discount__pill');
    discountPills.forEach(pill => {
      const code = pill.dataset.discountCode;
      const discountAmountElement = pill.querySelector('.discount-amount');
      
      if (code && discountAmountElement) {
        const discountApp = window.cartData.cart_level_discount_applications.find(app => app.title === code);
        if (discountApp) {
          discountAmountElement.textContent = ` -${this.formatMoney(discountApp.total_allocated_amount)}`;
        }
      }
    });
  }

  updateCartTotals(cartData) {
    // Update subtotal - find by looking for elements containing "Subtotal"
    const subtotalElements = document.querySelectorAll('.cart-drawer .h-stack');
    subtotalElements.forEach(element => {
      const textElement = element.querySelector('.text-subdued');
      if (textElement && textElement.textContent.includes('Subtotal')) {
        const priceElement = element.querySelector('.text-subdued:last-child');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(cartData.items_subtotal_price);
        }
      }
    });

    // Update total - find by looking for elements containing "Total"
    const totalElements = document.querySelectorAll('.cart-drawer .h-stack');
    totalElements.forEach(element => {
      const textElement = element.querySelector('.h5');
      if (textElement && textElement.textContent.includes('Total')) {
        const priceElement = element.querySelector('.h5:last-child');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(cartData.total_price);
        }
      }
    });

    // Update discount applications
    this.updateDiscountApplications(cartData.discount_applications || []);
  }

  updateDiscountApplications(discountApplications) {
    // Find the discount applications section
    const discountSection = document.querySelector('.cart-drawer');
    if (!discountSection) return;

    // Update existing discount amount elements in the component
    const discountAmountElements = this.querySelectorAll('.discount-amount');
    discountAmountElements.forEach((element, index) => {
      if (discountApplications[index]) {
        element.textContent = `-${this.formatMoney(discountApplications[index].total_allocated_amount)}`;
      } else {
        element.textContent = '';
      }
    });

    // Also update discount amounts in cart drawer
    const cartDiscountAmountElements = discountSection.querySelectorAll('.discount-amount');
    cartDiscountAmountElements.forEach((element, index) => {
      if (discountApplications[index]) {
        element.textContent = `-${this.formatMoney(discountApplications[index].total_allocated_amount)}`;
      } else {
        element.textContent = '';
      }
    });

    // Remove existing discount applications
    const existingDiscounts = discountSection.querySelectorAll('.h-stack');
    existingDiscounts.forEach(el => {
      const badge = el.querySelector('.badge');
      if (badge && (el.textContent.includes('discount') || el.textContent.includes('🎫'))) {
        el.remove();
      }
    });
  }

  updateCartCount(itemCount) {
    // Update cart count in header if it exists
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = itemCount;
      element.style.display = itemCount > 0 ? 'block' : 'none';
    });
  }

  formatMoney(cents) {
    // Simple money formatting - you might want to use Shopify's money formatting
    return `$${(cents / 100).toFixed(2)}`;
  }

  hideErrors() {
    // Hide the main error container
    const errorContainer = this.querySelector('.cart-discount__error');
    if (errorContainer) {
      errorContainer.classList.add('hidden');
    }
    
    // Hide all error text elements
    const errorElements = this.querySelectorAll('.cart-discount__error-text');
    errorElements.forEach(element => {
      element.classList.add('hidden');
    });
  }

  handleDiscountError(type) {
    console.log('handleDiscountError called with type:', type);
    
    // Show the main error container
    const errorContainer = this.querySelector('.cart-discount__error');
    if (errorContainer) {
      console.log('Found error container, removing hidden class');
      errorContainer.classList.remove('hidden');
    } else {
      console.log('Error container not found');
    }
    
    // Show the specific error text
    const errorElement = this.querySelector(`.cart-discount__error--${type}`);
    if (errorElement) {
      console.log('Found error element, removing hidden class');
      errorElement.classList.remove('hidden');
    } else {
      console.log(`Error element .cart-discount__error--${type} not found`);
    }
  }

  async validateDiscountCodes(discountCodes, cartData) {
    const validCodes = [];
    
    for (const code of discountCodes) {
      // Check if the code is actually applied in the cart data
      let isValid = false;
      
      // Check discount_applications
      if (cartData.discount_applications && Array.isArray(cartData.discount_applications)) {
        const discountApp = cartData.discount_applications.find(app => app.title === code);
        if (discountApp && discountApp.applicable !== false) {
          isValid = true;
        }
      }
      
      // Check cart_level_discount_applications
      if (!isValid && cartData.cart_level_discount_applications && Array.isArray(cartData.cart_level_discount_applications)) {
        const discountApp = cartData.cart_level_discount_applications.find(app => app.title === code);
        if (discountApp && discountApp.applicable !== false) {
          isValid = true;
        }
      }
      
      // Check discount_codes
      if (!isValid && cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
        const discountCode = cartData.discount_codes.find(dc => {
          if (typeof dc === 'object' && dc.code) {
            return dc.code === code && dc.applicable !== false;
          }
          return dc === code;
        });
        if (discountCode) {
          isValid = true;
        }
      }
      
      if (isValid) {
        validCodes.push(code);
      } else {
        console.log(`Invalid discount code removed: ${code}`);
      }
    }
    
    return validCodes;
  }

  async removeInvalidDiscounts(allCodes, validCodes) {
    try {
      // Create a comma-separated string of only valid codes
      const validCodesString = validCodes.join(',');
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: validCodesString,
        }),
      });

      if (!response.ok) {
        console.error('Failed to remove invalid discount codes');
        return;
      }

      console.log('Successfully removed invalid discount codes');
      
      // Refresh cart data
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        await this.updateComponentWithCartData(cartData);
      }
    } catch (error) {
      console.error('Error removing invalid discount codes:', error);
    }
  }

  handleCartChange(event) {
    // Update discount amounts when cart changes
    if (event.detail && event.detail.cart) {
      window.cartData = event.detail.cart;
      setTimeout(() => {
        this.updateDiscountAmounts();
      }, 50);
    }
  }

  getExistingDiscounts() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    
    console.log('Found discount pills:', discountPills.length);
    
    discountPills.forEach(pill => {
      const code = pill.dataset.discountCode;
      if (code) {
        discountCodes.push(code);
      }
    });

    console.log('Existing discount codes:', discountCodes);
    return discountCodes;
  }
}

// Register the custom element
if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
